import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  FormControl,
  Select,
  Paragraph,
  Checkbox,
  Table,
  Button,
  Notification,
  Textarea,
  Tabs,
  TextInput,
  Spinner,
  Badge,
  Skeleton,
  Note,
} from "@contentful/f36-components";
import { DialogAppSDK } from "@contentful/app-sdk";
import { createClient } from "contentful-management";

import { Environment, Space } from "contentful-management/types";
import { Options, documentToHtmlString } from "@contentful/rich-text-html-renderer";
import { BLOCKS, INLINES } from "@contentful/rich-text-types";
import { translateMultipleEntries } from "../../api/backendClient";

interface DialogProps {
  sdk: DialogAppSDK;
}

const TRANSLATION_SERVICE_OPTIONS: string[] = [
  "Human Translation",
  "AI-Powered Translation + Post-Editing",
  "AI-Powered Translation"
];

const NewRequest = (props: DialogProps) => {
  // Gets the Contentful SDK
  const { sdk } = props;
  const cma = createClient({
    apiAdapter: sdk.cmaAdapter,
  });

  let [sourceLanguage, setSourceLanguage] = React.useState(sdk.locales.available[0]);
  let [targetLanguages, setTargetLanguages] = React.useState<string[]>([]);
  let [title, setTitle] = React.useState("");
  let [comments, setComments] = React.useState("");
  let [translationService, setTranslationService] = React.useState(TRANSLATION_SERVICE_OPTIONS[0]);
  let [exportReferencedEntries, setExportReferencedEntries] = React.useState(true);
  let [selectedTab, setSelectedTab] = React.useState("gpi-info-tab");
  let [shouldHideButton, setShouldHideButton] = React.useState(false);
  let [shouldHideSpinner, setShouldHideSpinner] = React.useState(true);
  let [progressMessage, setProgressMessage] = React.useState("Submitting request...");
  let [allEntries, setAllEntries] = React.useState<any[]>([]);
  let [entries, setEntries] = React.useState<any[]>([]);
  let [environment, setEnvironment] = React.useState<any>();
  let [contentTypes, setContentTypes] = React.useState([]);
  let [dataLoaded, setDataLoaded] = React.useState(false);
  const [checks, setChecks] = useState<any[]>([]);
  // The connector will ignore exporting related entries from the following types
  let ignoreRelatedEntriesContentTypes = ["lodge", "page", "pageGeneric", "pageHomepage", "contentArticle", "pages", "promotions", "skill" , "productPage", "pg-page", "appcontent", "AppContent"];

  const getSpace = useCallback(() => {
    return cma.getSpace(sdk.ids.space);
  }, [cma, sdk.ids.space]);

  const getEnvironment = useCallback(
    (space: Space) => {
      return space.getEnvironment(sdk.ids.environment);
    },
    [sdk.ids.environment],
  );

  const OptionsRichtext = useMemo(() => {
    const options: Partial<Options> = {
      renderNode: {
        [BLOCKS.EMBEDDED_ENTRY]: (node: any) => {
          const customTag = `<embedded-entry id="${node.data.target.sys.id}" ></embedded-entry>`;
          return customTag;
        },
        [INLINES.EMBEDDED_ENTRY]: (node, children) => {
          const customTagHtml = `<inline-entry id="${node.data.target.sys.id}"></inline-entry>`;
          return customTagHtml;
        },
      },
      preserveWhitespace: true,
    };

    return options;
  }, []);

  const getContentTypes = useCallback((environment: Environment) => {
    return environment.getContentTypes();
  }, []);

  const addEnvironmentEntries = useCallback(
    (envEntries: any, contentType: any) => {
      let existing: any = allEntries.filter((e: any) => e.contentType === contentType.sys.id);

      if (existing && existing[0] && existing[0].entries.length > 0) {
        let existingEntries: any[] = existing[0].entries;
        let newEntries: any[] = envEntries.items;
        let combinedEntries: any[] = existingEntries.concat(newEntries);

        let updatedEntries = allEntries.map((item: any) => {
          if (item.contentType === contentType.sys.id) {
            item.entries = combinedEntries;
          }
          return item;
        });

        setAllEntries(updatedEntries);
        setEntries(combinedEntries);
      } else {
        let newItem: any = {
          contentType: contentType.sys.id,
          entries: envEntries.items,
        };

        allEntries.push(newItem);
        setAllEntries(allEntries);
        setEntries(envEntries.items);
      }
    },
    [allEntries],
  );

  const setContentTypeFields = useCallback(
    (entry: any, contentType: any) => {
      let displayField: string = "";
      let displayType: string = "";
      let typeInternalName: string = "";
      let localizedFields: string[] = [];
      let richTextFields: string[] = [];

      if (contentType) {
        displayField = contentType.displayField;
        displayType = contentType.name;
        typeInternalName = contentType.sys.id;

        contentType.fields.forEach((field: any) => {
          // checks localized fields
          if (field.localized) {
            localizedFields.push(field.id);
          }
          // checks richtext fields
          if (field.type === "RichText") {
            richTextFields.push(field.id);
          }
        });
      }

      // Display field
      if (displayField && entry.fields && entry.fields[displayField]) {
        entry.displayTitle = entry.fields[displayField][sdk.locales.default];
      } else {
        entry.displayTitle = "Untitled - ID: " + entry.sys.id;
      }

      // Display type
      if (displayType) {
        entry.displayType = displayType;
      } else {
        entry.displayType = entry.sys.type;
      }

      // Type Internal Name
      if (typeInternalName) {
        entry.typeInternalName = typeInternalName;
      } else {
        entry.typeInternalName = displayType;
      }

      // Localized fields
      if (localizedFields) {
        entry.localizedFields = localizedFields;
      }

      // RichText fields
      if (richTextFields) {
        entry.richTextFields = richTextFields;
      }

      return displayField;
    },
    [sdk.locales.default],
  );

  const getEnvironmentEntries = useCallback(
    (environment: Environment, contentType: any, skipEntries: number): any => {
      let query: any = {
        skip: skipEntries,
      };

      if (contentType) {
        query.content_type = contentType.sys.id;
      }

      return environment
        .getEntries(query)
        .then((resEntries) => {
          resEntries.items = resEntries.items.filter((entry: any) => {
            return !isEmptyObject(entry) && !isEmptyObject(entry.fields);
          });

          resEntries.items.forEach((entry: any) => {
            setContentTypeFields(entry, contentType);
          });

          addEnvironmentEntries(resEntries, contentType);

          if (resEntries.total > resEntries.skip + resEntries.limit) {
            return getEnvironmentEntries(environment, contentType, resEntries.skip + resEntries.limit);
          }
        })
        .catch((error: any) => {
          setDataLoaded(true);
        });
    },
    [addEnvironmentEntries, setContentTypeFields],
  );

  const addContentTypes = (contentTypes: any) => {
    setContentTypes(contentTypes.items);
    return contentTypes.items;
  };

  const onSelectSourceLanguage = (langCode: string) => {
    setSourceLanguage(langCode);
  };

  const onSelectTargetLanguage = (checked: boolean, langCode: string) => {
    if (checked) {
      if (!targetLanguages.includes(langCode)) {
        setTargetLanguages([...targetLanguages, langCode]);
      }
    } else {
      setTargetLanguages(targetLanguages.filter((e) => e !== langCode));
    }
  };

  const onSelectContentItem = (checked: boolean, item: any) => {
    item.checked = checked;

    if (checked) {
      setChecks([...checks, item.sys.id]);
    } else {
      setChecks(checks.filter((id: any) => id !== item.sys.id));
    }
  };

  const isChecked = (id: any): boolean => {
    let res = false;

    const finded = checks.find((item: any) => item === id);

    res = !!finded;

    return res;
  };

  const onSelectAllEntries = (checked: boolean) => {
    entries.forEach((item: any) => {
      item.checked = checked;
    });
    setEntries(entries);

    let entryId = [];
    if (checked) {
      entryId = entries.map((item: any) => {
        return item.sys.id;
      });
    }
    setChecks(entryId);
  };

  const onSelectContentTypeFilter = (contentTypeId: string) => {
    let filteredEntries = allEntries.filter((item: any) => {
      return item.contentType === contentTypeId;
    });

    if (filteredEntries.length > 0) {
      setEntries(filteredEntries[0].entries);
    } else {
      const matchingContentTypes: any = contentTypes.filter((c: any) => c.sys.id === contentTypeId);
      if (matchingContentTypes && matchingContentTypes.length > 0) {
        setEntries([]);
        setDataLoaded(false);
        getEnvironmentEntries(environment, matchingContentTypes[0], 0).then(() => {
          setDataLoaded(true);
        });
      }
    }
  };

  const onChangeTitle = (newTitle: string) => {
    setTitle(newTitle);
  };

  const onChangeComments = (newComments: string) => {
    setComments(newComments);
  };

  const isEmptyObject = (obj: any) => {
    for (let prop in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, prop)) {
        return false;
      }
    }
    return true;
  };

  const getRichtTextEmbeddedEntries = (fieldValue: any, entriesToFetch: any[]) => {
    if (fieldValue.content && Array.isArray(fieldValue.content)) {
      fieldValue.content.forEach((contentEntry: any) => {
        if (
          contentEntry.data &&
          contentEntry.data.target &&
          contentEntry.nodeType &&
          contentEntry.nodeType.includes("embedded-entry")
        ) {
          contentEntry.data.target.embedded = true;
          entriesToFetch.push(contentEntry.data.target);
        }

        if (contentEntry.content && Array.isArray(contentEntry.content)) {
          getRichtTextEmbeddedEntries(contentEntry, entriesToFetch);
        }
      });
    }
  };

  const getRelatedEntries = (entry: any) => {
    let relatedEntriesIds: any[] = [];

    for (let field in entry.fields) {
      // If the given field does NOT match with any localized field, then continues to the next field
      // this was commented for ZOGO finance, as they requested that references should be exported
      // even though the field is not marked as localized field
      //let matches = entry.localizedFields.filter((locField:any) => { return locField === field});
      //if(matches.length === 0) {
      //continue;
      //}

      // Gets the field value
      let fieldValue = entry.fields[field][sourceLanguage];

      // Parses the references and embedded entries
      let entriesToFetch: any[] = [];
      if (Array.isArray(fieldValue)) {
        entriesToFetch = fieldValue;

        entriesToFetch.forEach((arrEntry: any) => {
          if (arrEntry.content && Array.isArray(arrEntry.content)) {
            // if the field is rich text, it will grab embedded entries
            getRichtTextEmbeddedEntries(arrEntry, entriesToFetch);
          }
        });
      } else {
        entriesToFetch.push(fieldValue);
        // if the field is rich text, it will grab embedded entries
        if (fieldValue) {
          getRichtTextEmbeddedEntries(fieldValue, entriesToFetch);
        }
      }

      // For each referenced entry
      entriesToFetch.forEach((entryToFech) => {
        // If the field is not a reference field, then it continues to the next field
        if (
          !entryToFech ||
          !entryToFech.sys ||
          entryToFech.sys.type !== "Link" ||
          entryToFech.sys.linkType !== "Entry"
        ) {
          return;
        }

        // if it arrives at this point, that means that the field is marked as translatable and
        // has a reference to another item. Then, gets the related entry from Contentful DB.

        // Checks if the entry was not already added to the list.
        let alreadyRetrieved = false;
        relatedEntriesIds.forEach((entryId: any) => {
          if (entryId.id === entryToFech.sys.id) {
            alreadyRetrieved = true;
          }
        });
        // If the entry was not added yet, adds it to the list
        if (!alreadyRetrieved) {
          relatedEntriesIds.push({
            id: entryToFech.sys.id,
            embedded: entryToFech.embedded,
          });
        }
      });
    }

    let relatedEntries: any[] = [];

    // If there are no related entries to fetch
    if (relatedEntriesIds.length === 0) {
      return new Promise((resolve) => {
        resolve(relatedEntries);
      });
    }

    // Creates the list of IDs to query the API
    let idsList = "";
    relatedEntriesIds.forEach((entryId: any) => {
      if (idsList === "") {
        idsList += entryId.id;
      } else {
        idsList += "," + entryId.id;
      }
    });

    // Gets all the related entries via the API
    return environment
      .getEntries({
        "sys.id[in]": idsList,
      })
      .then((res: any) => {
        if (res && res.items) {
          res.items.forEach((related: any) => {
            if (
              related &&
              !relatedEntries.some((r: any) => {
                return r.sys.id === related.sys.id;
              })
            ) {
              let contentType = contentTypes.filter((c: any) => {
                return c.sys.id === related.sys.contentType.sys.id;
              });
              setContentTypeFields(related, contentType[0]);
              relatedEntries.push(related);
            }
          });
        }

        return relatedEntries;
      });
  };

  const getRelatedEntriesRecursive = (
    entry: any,
    relatedEntries: any[],
    level: number,
    rateLimiter: any,
  ) => {
    let promises: Promise<any>[] = [];

    return rateLimiter(() => getRelatedEntries(entry)).then((res: any) => {
      let index = 1;
      const queue = multiQueue(6); // most browsers I think limit to 6 simultaneous AJAX anyway

      res.forEach((related: any) => {
        if (
          !relatedEntries.some((r: any) => {
            return r.sys.id === related.sys.id;
          })
        ) {
          /*var isPublished = related.sys && related.sys.publishedAt;*/
          const shouldIgnoreType = ignoreRelatedEntriesContentTypes.some((t: string) => {
            return t === related.typeInternalName;
          });

          if (!shouldIgnoreType /* && isPublished*/) {
            relatedEntries.push(related);

            let promise = queue(() => {
              return getRelatedEntriesRecursive(related, relatedEntries, level + 1, rateLimiter);
            });

            promises.push(promise);

            index++;
          }
        }
      });

      return Promise.all(promises).then((results) => {
        results.forEach((res) => {
          res.forEach((related: any) => {
            if (
              !relatedEntries.some((r: any) => {
                return r.sys.id === related.sys.id;
              })
            ) {
              relatedEntries.push(related);
            }
          });
        });

        return relatedEntries;
      });
    });
  };

  const createRateLimiter = (requestsPerSecond: number) => {
    let queue: Array<() => Promise<any>> = [];
    let processing = false;
    let requestCount = 0;
    let lastResetTime = Date.now();

    const processQueue = async () => {
      if (processing || queue.length === 0) {
        return;
      }

      processing = true;

      while (queue.length > 0) {
        const now = Date.now();
        const timeElapsed = now - lastResetTime;

        // Reset counter every second
        if (timeElapsed >= 1000) {
          requestCount = 0;
          lastResetTime = now;
        }

        // If we've hit the rate limit, wait until the next second
        if (requestCount >= requestsPerSecond) {
          const waitTime = 1000 - timeElapsed;
          await new Promise((resolve) => setTimeout(resolve, waitTime));
          requestCount = 0;
          lastResetTime = Date.now();
        }

        // Process the next request
        const request = queue.shift();
        if (request) {
          requestCount++;
          await request();
        }
      }

      processing = false;
    };

    return (fn: () => Promise<any>) => {
      return new Promise((resolve, reject) => {
        queue.push(async () => {
          try {
            const result = await fn();
            resolve(result);
          } catch (error) {
            reject(error);
          }
        });
        processQueue();
      });
    };
  };

  const getSelectedEntries = (): any => {
    let res: any[] = [];
    let promises: Promise<any>[] = [];
    const queue = multiQueue(6); // most browsers I think limit to 6 simultaneous AJAX anyway

    // Contentful API limit: 55 requests/second
    // Set to 50 to leave a safety margin
    const rateLimiter = createRateLimiter(50);

    allEntries.forEach((item: any) => {
      item.entries
        .filter((entry: any): any[] => {
          return entry.checked;
        })
        .forEach((entry: any) => {
          res.push(entry);
          if (exportReferencedEntries) {
            let promise: Promise<any> = queue(() => getRelatedEntriesRecursive(entry, res, 1, rateLimiter));
            promises.push(promise);
          }
        });
    });

    return Promise.all(promises).then((results) => {
      results.forEach((relatedEntries) => {
        relatedEntries.forEach((related: any) => {
          if (
            !res.some((r: any) => {
              return r.sys.id === related.sys.id;
            })
          ) {
            res.push(related);
          }
        });
      });
      return res;
    });
  };

  const multiQueue = (max: number) => {
    max = isNaN(max) || max < 1 ? 1 : max;

    const q = new Array(max).fill(0).map(() => Promise.resolve());

    let index = 0;

    const add = (cb: any, ...args: any) => {
      index = (index + 1) % max;
      return (q[index] = q[index].then(() => cb(...args)));
    };

    return add;
  };

  const prepareSelectedItemsForTranslation = (): any => {
    return getSelectedEntries().then((selectedEntries: any) => {
      return selectedEntries.map((entry: any) => {
        // Gets the content type
        let contentTypeFiltered = contentTypes.filter((c: any) => {
          return c.sys.id === entry.sys.contentType.sys.id;
        });

        // Sets the fields
        let fields: any = {};
        for (let field in entry.fields) {
          if (!entry.localizedFields) {
            continue;
          }

          let matches = entry.localizedFields.filter((locField: any) => {
            return locField === field;
          });
          if (matches.length > 0) {
            // sets the value
            let fieldValue = entry.fields[field][sourceLanguage];

            if (Array.isArray(fieldValue)) {
              fieldValue = fieldValue.filter((item: any) => {
                return !(
                  item &&
                  item.sys &&
                  item.sys.type === "Link" &&
                  (item.sys.linkType === "Entry" || item.sys.linkType === "Asset")
                );
              });

              if (fieldValue.length === 0) {
                continue;
              }
            }

            if (
              entry.fields[field][sourceLanguage] &&
              entry.fields[field][sourceLanguage].sys &&
              entry.fields[field][sourceLanguage].sys.type === "Link" &&
              (entry.fields[field][sourceLanguage].sys.linkType === "Entry" ||
                entry.fields[field][sourceLanguage].sys.linkType === "Asset")
            ) {
              continue;
            }

            if (entry.richTextFields) {
              let isRichText = entry.richTextFields.filter((rtField: any) => {
                return rtField === field;
              });
              if (isRichText.length > 0) {
                fieldValue = documentToHtmlString(fieldValue, OptionsRichtext);
              }
            }

            // Checks if field is disabled
            let isDisabled = false;
            if (contentTypeFiltered.length > 0 && contentTypeFiltered[0]) {
              let entryContentType: any = contentTypeFiltered[0];
              let currentFieldDefinition = entryContentType.fields.filter((f: any) => {
                return f.id === field;
              });
              isDisabled = currentFieldDefinition.length > 0 && currentFieldDefinition[0].disabled;
            }

            // If the field is not disabled, adds it to the export
            if (!isDisabled) {
              fields[field] = fieldValue;
            }
          }
        }

        // Sets the displayTitle
        let displayTitle = entry.sys.id;

        if (contentTypeFiltered.length > 0) {
          let entryContentType: any = contentTypeFiltered[0];
          let displayField: string = entryContentType.displayField;

          if (displayField && entry.fields && entry.fields[displayField]) {
            displayTitle = entry.fields[displayField][sdk.locales.default];
          }
        }

        // Returns the formatted item for export
        return {
          id: entry.sys.id,
          contentType: entry.typeInternalName,
          fields: fields,
          displayTitle: displayTitle,
        };
      });
    });
  };

   const sendForTranslation = async () => {
    // Validations
    if (!title || !title.trim()) {
      Notification.setDuration(2000);
      Notification.error("Please, specify a title for the request.");
      return;
    }

    if (!translationService) {
      Notification.setDuration(2000);
      Notification.error("Please, select a translation service.");
      return;
    }

    if (!sourceLanguage) {
      Notification.setDuration(2000);
      Notification.error("Please, select a source language.");
      return;
    }

    if (!targetLanguages || targetLanguages.length === 0) {
      Notification.setDuration(2000);
      Notification.error("Please, select at least one target language.");
      return;
    }

    if (
      targetLanguages.filter((lang) => {
        return lang === sourceLanguage;
      }).length > 0
    ) {
      Notification.setDuration(2000);
      Notification.error("The source language can not be selected as a target language.");
      return;
    }

    // Validate at least one entry is checked before making any API calls
    const hasChecked = allEntries.some((item: any) =>
      item.entries.some((entry: any) => entry.checked)
    );

    if (!hasChecked) {
      Notification.setDuration(2000);
      Notification.error("Please, select at least one content item.");
      return;
    }

    // Shows the spinner while submitting
    setShouldHideButton(true);
    setShouldHideSpinner(false);
    setProgressMessage("Submitting request...");

    try {
      // Get selected entries — includes related entries recursively if exportReferencedEntries is enabled
      const selectedEntries = await getSelectedEntries();
      const selectedEntryIds = selectedEntries.map((entry: any) => entry.sys.id);

      // Submit the job to backend
      const submitResult = await translateMultipleEntries(
        sdk,
        selectedEntryIds,
        sourceLanguage,
        targetLanguages,
        translationService,
        title,
        comments
      );

      if (!submitResult.success || !submitResult.data?.jobId) {
        throw new Error(submitResult.error || "Failed to submit translation request");
      }

      const jobId = submitResult.data.jobId;

      // Close modal and pass jobId to parent for polling
      // Parent (OpenModals.tsx) will handle notifications and polling
      sdk.close({ jobId, success: true });

    } catch (error: any) {
      console.error("Translation error:", error);
      Notification.setDuration(0);
      Notification.error(
        "An error occurred while trying to submit the request. Details: " + error.message
      );
      setShouldHideButton(false);
      setShouldHideSpinner(true);
      setProgressMessage("Submitting request...");
    }
  };

  const isEmpty = (entry: any) => {
    for (let prop in entry) {
      if (Object.prototype.hasOwnProperty.call(entry, prop)) {
        return false;
      }
    }

    return JSON.stringify(entry) === JSON.stringify({});
  };

  useEffect(() => {
    const getEntries = () => {
      return getSpace()
        .then(getEnvironment)
        .then((environment) => {
          setEnvironment(environment);
          return getContentTypes(environment)
            .then(addContentTypes)
            .then((contentTypes) => {
              let defaultContentType: any = undefined!;
              if (contentTypes && contentTypes.length > 0) {
                defaultContentType = contentTypes[0];
              }
              setDataLoaded(false);
              return getEnvironmentEntries(environment, defaultContentType, 0).then(() => {
                setDataLoaded(true);
              });
            });
        });
    };

    getEntries();
  }, []);

  return (

      <div
        style={{
          width: "95%",
          marginLeft: "auto",
          marginRight: "auto",
        }}
      >
        <div style={{ height: "520px" }} className="f36-margin-bottom--l">
          <Tabs currentTab={selectedTab} onTabChange={(tabId) => setSelectedTab(tabId)}>
            <Tabs.List>
              <Tabs.Tab panelId="gpi-info-tab">Information</Tabs.Tab>
              <Tabs.Tab panelId="gpi-content-tab">Content</Tabs.Tab>
            </Tabs.List>

            <Tabs.Panel id="gpi-info-tab">
            <FormControl marginTop="spacingM">
              <FormControl.Label>Translation Service:</FormControl.Label>
              <Select
                id="translation-service"
                name="translation-service"
                value={translationService}
                onChange={(e) => setTranslationService(e.target.value)}
              >
                {TRANSLATION_SERVICE_OPTIONS.map((option) => {
                  return (
                    <Select.Option value={option} key={"option-" + option}>
                      {option}
                    </Select.Option>
                  );
                })}
              </Select>
            </FormControl>
            <FormControl>
              <FormControl.Label>Title:</FormControl.Label>
              <TextInput name="quote-title" type="text" value={title} onChange={(e) => onChangeTitle(e.target.value)} />
            </FormControl>
            <FormControl>
              <FormControl.Label>Source Language:</FormControl.Label>
              <Select
                id="source-language"
                name="source-language"
                value={sourceLanguage}
                onChange={(e) => onSelectSourceLanguage(e.target.value)}
              >
                {Object.keys(sdk.locales.names).map((key) => {
                  return (
                    <Select.Option value={key} key={"option-" + key}>
                      {sdk.locales.names[key]}
                    </Select.Option>
                  );
                })}
              </Select>
            </FormControl>
            <FormControl>
              <FormControl.Label>Target Languages:</FormControl.Label>
              <div
                style={{
                  maxHeight: "120px",
                  minHeight: "40px",
                }}
              >
                {Object.keys(sdk.locales.names).map((key) => {
                  return (
                    <div
                      key={"div-chk-" + key}
                      style={{
                        display: "inline-block",
                        marginRight: "20px",
                      }}
                    >
                      <Checkbox
                        id={"chk-" + key}
                        name={"chk-" + key}
                        isChecked={targetLanguages.includes(key)}
                        onChange={(e) => onSelectTargetLanguage(e.target.checked, key)}
                      >
                        {sdk.locales.names[key]}
                      </Checkbox>
                    </div>
                  );
                })}
              </div>
            </FormControl>
            <FormControl>
              <FormControl.Label>Comments:</FormControl.Label>
              <Textarea
                name="notes"
                id="textarea-notes"
                rows={2}
                value={comments}
                onChange={(e) => onChangeComments(e.target.value)}
              />
            </FormControl>
          </Tabs.Panel>

          <Tabs.Panel id="gpi-content-tab">
            <div
              style={{
                width: "48%",
                display: "inline-block",
                marginTop: "16px",
              }}
            >
              <FormControl>
                <FormControl.Label>Type:</FormControl.Label>
                <Select
                  id="contentTypeFilter"
                  name="contentTypeFilter"
                  onChange={(e) => onSelectContentTypeFilter(e.target.value)}
                >
                  {contentTypes.map((type: any) => {
                    return (
                      <Select.Option value={type.sys.id} key={"option-" + type.name}>
                        {type.name}
                      </Select.Option>
                    );
                  })}
                </Select>
              </FormControl>
            </div>
            <div
              style={{
                width: "4%",
                display: "inline-block",
              }}
            ></div>
            {/* <div style={{
                width: "48%",
                display: "inline-block",
              }}
            >
              <FormLabel htmlFor="filter-entry-title">Export Referenced Entries:</FormLabel>
              <div style={{ maxHeight: "120px", minHeight: "60px" }} className="f36-margin-top--m">
                <div style={{ display: "inline-block", marginRight: "20px" }}>
                  <RadioButtonField
                    name="rb-export-related"
                    id="rb-export-related"
                    value="yes"
                    checked={true}
                    onChange={(e) => onSelectExportReferencedEntries(e.target.checked)}
                    labelText="JSON"
                  />
                </div>
                <div style={{ display: "inline-block", marginRight: "20px" }}>
                  <RadioButtonField
                    name="rb-export-related"
                    id="rb-export-related"
                    value="no"
                    onChange={(e) => onSelectExportReferencedEntries(e.target.checked)}
                    labelText="No"
                  />
                </div>
              </div>
            </div> */}
            <div style={{ width: "25%", display: "inline-block" }}>
              <Checkbox
                name="ckbx-export-related-item"
                id="ckbx-export-related-item"
                onChange={(e) => setExportReferencedEntries(!exportReferencedEntries)}
                isChecked={exportReferencedEntries}
              >
                Export related items
              </Checkbox>
            </div>
            <div style={{ marginTop: "15px", height: "320px", overflowY: "scroll" }}>
              <Table
                style={{
                  width: "100%",
                }}
              >
                <Table.Head>
                  <Table.Row>
                    <Table.Cell>Title</Table.Cell>
                    {/*<Table.Cell style={{ width: '20%' }}>Type</Table.Cell>*/}
                    <Table.Cell
                      style={{
                        width: "15%",
                      }}
                    >
                      Select All
                      <Checkbox
                        id={"chk-SelectAll"}
                        name={"chk-SelectAll"}
                        isChecked={entries.length > 0 && entries.every((entry: any) => entry.checked)}
                        onChange={(e) => {
                          onSelectAllEntries(e.target.checked);
                        }}
                      >
                      </Checkbox>
                    </Table.Cell>
                  </Table.Row>
                </Table.Head>
                <Table.Body>
                  <>
                    {dataLoaded &&
                      entries.map((entry: any) => {
                        return (
                          <Table.Row key={"t-row-" + entry.sys.id}>
                            <Table.Cell>
                              {entry.displayTitle}{" "}
                              <Badge variant={entry.sys.publishedAt ? "positive" : "warning"}>
                                {entry.sys.publishedAt ? "PUBLISHED" : "DRAFT"}
                              </Badge>
                            </Table.Cell>
                            {/*<Table.Cell>{entry.displayType}</Table.Cell>*/}
                            <Table.Cell>
                              <Checkbox
                                id={"chk-entries-" + entry.sys.id}
                                name={"chk-entries-" + entry.sys.id}
                                isChecked={isChecked(entry.sys.id)}
                                onChange={(e) => onSelectContentItem(e.target.checked, entry)}
                              >
                              </Checkbox>
                            </Table.Cell>
                          </Table.Row>
                        );
                      })}
                  </>
                  <>{!dataLoaded && <Skeleton.Row rowCount={5} columnCount={2} />}</>
                </Table.Body>
              </Table>
              <>
                {dataLoaded && entries.length === 0 && (
                  <Note
                    style={{
                      marginTop: "15px",
                    }}
                  >
                    There are no entries for the specified filters.
                  </Note>
                )}
              </>
            </div>
          </Tabs.Panel>
          </Tabs>
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", marginTop: "10px", marginBottom: "5px" }}>
          <div id="spinner" className={shouldHideSpinner ? "hidden-spinner" : ""}>
            <Paragraph style={{ paddingBottom: "0px", paddingTop: "0px", marginRight: "10px" }}>
              <b>{progressMessage}</b> <Spinner />
            </Paragraph>
          </div>
          <Button
            variant="primary"
            size="small"
            className={shouldHideButton ? "hidden-spinner" : ""}
            onClick={() => sendForTranslation()}
            type="submit"
          >
            Send for Translation
          </Button>
        </div>
      </div>
  );
};

export default NewRequest;
